import { useEffect, useRef, useState } from "react";

const DEBOUNCE_TIME = 500;

interface IUseFormBuilderDebouncedUpdateArgs<TValue> {
  defaultValue: TValue;
  value: TValue;
}

interface IFormBuilderDebouncedUpdateState<TValue> {
  value: TValue;
  active: boolean;
}

type TFormBuilderDebouncedUpdateDispatch<TValue> = (nextValue: TValue) => void;

type TFormBuilderDebouncedUpdateActivate = () => void;
type TFormBuilderDebouncedUpdateChange<TValue> = (nextValue: TValue, dispatch: TFormBuilderDebouncedUpdateDispatch<TValue>) => void;
type TFormBuilderDebouncedUpdateCommit<TValue> = (dispatch: TFormBuilderDebouncedUpdateDispatch<TValue>) => void;

interface IUseFormBuilderDebouncedUpdate<TValue> {
  state: IFormBuilderDebouncedUpdateState<TValue>;
  activate: TFormBuilderDebouncedUpdateActivate;
  change: TFormBuilderDebouncedUpdateChange<TValue>;
  commit: TFormBuilderDebouncedUpdateCommit<TValue>;
}

export function useFormBuilderDebouncedUpdate<TValue extends string | number | boolean>(args: IUseFormBuilderDebouncedUpdateArgs<TValue>): IUseFormBuilderDebouncedUpdate<TValue> {
  const debounceTimeoutRef = useRef<NodeJS.Timeout>(null);
  
  const [state, setState] = useState<IFormBuilderDebouncedUpdateState<TValue>>({
    value: args.defaultValue,
    active: false,
  });

  useEffect(() => {
    return () => clearTimeout(debounceTimeoutRef.current);
  }, []);

  function clearDebounce() {
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = null;
  }

  const activate: TFormBuilderDebouncedUpdateActivate = () => {
    clearDebounce();

    setState({
      value: args.value,
      active: true,
    });
  };

  const change: TFormBuilderDebouncedUpdateChange<TValue> = (nextValue, dispatch) => {
    setState(prev => ({
      ...prev,
      value: nextValue,
    }));

    clearDebounce();

    if (nextValue !== args.value) {
      debounceTimeoutRef.current = setTimeout(() => {
        dispatch(nextValue);
      }, DEBOUNCE_TIME);
    }
  };

  const commit: TFormBuilderDebouncedUpdateCommit<TValue> = dispatch => {
    clearDebounce();

    if (state.value !== args.value) {
      dispatch(state.value);
    }

    setState({
      value: args.defaultValue,
      active: false,
    });
  };

  return {
    state,
    activate,
    change,
    commit,
  };
}